import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faHandshake } from '@fortawesome/free-solid-svg-icons';

const PartnerViewTab = ({
  meetings,
  partnerParticipants,
  timeSlots,
  stickyHeaderCellStyle,
  stickyBodyCellStyle,
  handleCellClick,
  formatTime,
  formatParticipantName
}) => {
  const partnerViewData = useMemo(() => {
    if (!meetings || !partnerParticipants) {
      return [];
    }
    const meetingsByPartnerParticipant = partnerParticipants.reduce(
      (acc, pp) => {
        if (!pp || !pp.id) return acc;
        const participantId = pp.id;
        acc[participantId] = {
          participantId: pp.id,
          partnerName: pp.partner?.name || 'N/A',
          participantName: pp, // Passer l'objet participant complet
          schedule: {}
        };
        timeSlots.forEach(slot => {
          acc[participantId].schedule[slot.start] = [];
        });
        return acc;
      },
      {}
    );

    meetings.forEach(meetingSlot => {
      if (
        !meetingSlot.partner_participants ||
        meetingSlot.partner_participants.length === 0
      )
        return;
      meetingSlot.partner_participants.forEach(pp => {
        if (!pp || !pp.id) return;
        const participantId = pp.id;
        if (meetingsByPartnerParticipant[participantId]) {
          const startTime = formatTime(meetingSlot.start_time);
          if (meetingsByPartnerParticipant[participantId].schedule[startTime]) {
            let companyDisplay = 'Entreprise Indisponible';
            if (
              meetingSlot.company_participants &&
              meetingSlot.company_participants.length > 0
            ) {
              const firstCompanyParticipant =
                meetingSlot.company_participants[0];
              companyDisplay =
                firstCompanyParticipant.company?.name ||
                formatParticipantName(firstCompanyParticipant) ||
                'Entreprise (détails manquants)';
            }
            meetingsByPartnerParticipant[participantId].schedule[
              startTime
            ].push({
              text: companyDisplay,
              meetingId: meetingSlot.id,
              is_reciprocal: meetingSlot.is_reciprocal,
              fullMeetingData: meetingSlot
            });
          }
        }
      });
    });
    return Object.values(meetingsByPartnerParticipant).sort((a, b) =>
      `${a.partnerName} ${formatParticipantName(
        a.participantName
      )}`.localeCompare(
        `${b.partnerName} ${formatParticipantName(b.participantName)}`
      )
    );
  }, [
    meetings,
    partnerParticipants,
    timeSlots,
    formatTime,
    formatParticipantName
  ]);

  if (!partnerViewData || partnerViewData.length === 0) {
    return (
      <div className="text-center py-3">
        <p className="text-muted">
          Aucun meeting à afficher pour les partenaires.
        </p>
      </div>
    );
  }

  return (
    <Table bordered hover striped size="sm" className="fs--1 meetings-table">
      <thead className="bg-200 text-900">
        <tr>
          <th style={{ ...stickyHeaderCellStyle, left: 0, minWidth: '170px' }}>
            Partenaire
          </th>
          <th
            style={{
              ...stickyHeaderCellStyle,
              left: '170px',
              minWidth: '200px'
            }}
          >
            Participant
          </th>
          {timeSlots.map(slot => (
            <th
              key={slot.start}
              className="text-center"
              style={{ minWidth: '120px' }}
            >
              <FontAwesomeIcon icon={faClock} className="me-1" />
              {slot.start}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {partnerViewData.map(data => (
          <tr
            key={`part-row-${data.participantId}`}
            className={data.participantName.is_absent ? 'table-danger' : ''} // Ajouter une classe si absent
          >
            <td
              style={{
                ...stickyBodyCellStyle,
                left: 0,
                minWidth: '170px',
                ...(data.participantName.is_absent && {
                  backgroundColor: '#f8d7da'
                })
              }}
            >
              <strong>{data.partnerName}</strong>
            </td>
            <td
              style={{
                ...stickyBodyCellStyle,
                left: '170px',
                minWidth: '200px',
                ...(data.participantName.is_absent && {
                  backgroundColor: '#f8d7da'
                })
              }}
            >
              <strong>{formatParticipantName(data.participantName)}</strong>
            </td>
            {timeSlots.map(slot => (
              <td
                key={`${data.participantId}-${slot.start}`}
                className="text-center"
                onClick={() => {
                  const meetingItems = data.schedule[slot.start];
                  if (meetingItems && meetingItems.length > 0) {
                    // Laisser les badges gérer
                  } else {
                    handleCellClick(null, slot, data.participantId, 'partner');
                  }
                }}
                style={{ cursor: 'pointer', minWidth: '120px' }}
              >
                {data.schedule[slot.start] &&
                data.schedule[slot.start].length > 0 ? (
                  data.schedule[slot.start].map((meetingItem, index) => (
                    <Badge
                      key={`${slot.start}-${meetingItem.meetingId}-${index}`}
                      bg="primary"
                      className="p-1 d-inline-flex align-items-center me-1 mb-1"
                      onClick={e => {
                        e.stopPropagation();
                        handleCellClick(meetingItem, slot);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {meetingItem.text}
                      {meetingItem.is_reciprocal ? (
                        <FontAwesomeIcon
                          icon={faHandshake}
                          className="ms-1"
                          title="Vœu réciproque"
                        />
                      ) : null}
                    </Badge>
                  ))
                ) : (
                  <span
                    className="text-muted"
                    style={{ display: 'block', width: '100%', height: '100%' }}
                    onClick={e => {
                      e.stopPropagation();
                      handleCellClick(
                        null,
                        slot,
                        data.participantId,
                        'partner'
                      );
                    }}
                  >
                    -
                  </span>
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

PartnerViewTab.propTypes = {
  meetings: PropTypes.array.isRequired,
  partnerParticipants: PropTypes.array.isRequired,
  timeSlots: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.string.isRequired,
      end: PropTypes.string.isRequired
    })
  ).isRequired,
  stickyHeaderCellStyle: PropTypes.object.isRequired,
  stickyBodyCellStyle: PropTypes.object.isRequired,
  handleCellClick: PropTypes.func.isRequired,
  formatTime: PropTypes.func.isRequired,
  formatParticipantName: PropTypes.func.isRequired
};

export default PartnerViewTab;
